// MiscTab.js
// Container component for the Misc tab in the DEL form
// Contains sub-tabs for Photos, SDS, Waste Disposal, and Client Signatures

import React from "react";
import { ToggleSwitch } from "../../../common/ToggleSwitch";
import { WasteDisposalTab } from "../../../wasteDisposalForm/wasteDisposalComponents";
import { PhotosTab } from "./PhotosTab";
import { ClientSignaturesTab } from "./ClientSignaturesTab";
import { SdsTab } from "./SdsTab";
import styles from "./MiscTab.module.css";

// Sub-tab button component
const MiscSubTabButton = ({ tabName, label, isActive, isDisabled, onClick }) => {
  const handleClick = () => {
    if (!isDisabled) {
      onClick(tabName);
    }
  };

  return (
    <button
      className={`${styles.subTabButton} ${isActive ? styles.active : ""} ${
        isDisabled ? styles.disabled : ""
      }`}
      onClick={handleClick}
      disabled={isDisabled}
      type="button"
    >
      {label}
    </button>
  );
};

// Toggle section component for the header
const MiscToggleSection = ({ miscProps, projectActions }) => {
  const handleWasteDisposalToggle = (checked) => {
    // The toggle controls whether the tab is enabled, which is inverse of "not required"
    // wasteDisposalNotRequired = true means tab is disabled
    // So we pass the inverse: checked = enabled, !checked = not required
    projectActions.setShowWasteDisposalTab(!checked, miscProps.emitPatch);
  };

  const handleClientSignaturesToggle = (checked) => {
    projectActions.setShowClientSignaturesTab(checked, miscProps.emitPatch);
  };

  return (
    <div className={styles.toggleSection}>
      <div className={styles.toggleRow}>
        <ToggleSwitch
          checked={!miscProps.delData.projectInfo.wasteDisposalNotRequired}
          onChange={handleWasteDisposalToggle}
          disabled={miscProps.delMode === "view"}
          label="Waste Disposal"
          id="waste-disposal-toggle"
        />
      </div>
      <div className={styles.toggleRow}>
        <ToggleSwitch
          checked={miscProps.delData.projectInfo.clientSignaturesRequired}
          onChange={handleClientSignaturesToggle}
          disabled={miscProps.delMode === "view"}
          label="Client Signatures"
          id="client-signatures-toggle"
        />
      </div>
    </div>
  );
};

// Main MiscTab component
export const MiscTab = ({ miscProps, actions }) => {
  const activeMiscTab = miscProps.miscUI?.activeMiscTab || "photos";

  // Determine which sub-tabs are enabled
  const isWasteDisposalEnabled =
    !miscProps.delData.projectInfo.wasteDisposalNotRequired;
  const isClientSignaturesEnabled =
    miscProps.delData.projectInfo.clientSignaturesRequired;

  // Handle sub-tab change
  const handleSubTabChange = (tabName) => {
    actions.misc.setActiveMiscTab(tabName);
  };

  // Build callbacks for WasteDisposalTab (matching existing pattern)
  const wasteDisposalCallbacks = {
    wdData: miscProps.wdData,
    wdMode: miscProps.delMode,
    wdBase: miscProps.wdBase,
    emitPatch: miscProps.emitPatch,
  };

  // Build callbacks for ClientSignaturesTab
  const clientSignaturesCallbacks = {
    delData: miscProps.delData,
    delMode: miscProps.delMode,
    clientSignaturesUI: miscProps.clientSignaturesUI,
    emitPatch: miscProps.emitPatch,
    ...actions.clientSignatures,
  };

  // Build callbacks for PhotosTab
  const photosCallbacks = {
    delData: miscProps.delData,
    delMode: miscProps.delMode,
    emitPatch: miscProps.emitPatch,
    photosUI: miscProps.photosUI,
    connectionStatus: miscProps.connectionStatus,
  };

  // Build callbacks for SdsTab
  const sdsCallbacks = {
    delData: miscProps.delData,
    delMode: miscProps.delMode,
    emitPatch: miscProps.emitPatch,
    connectionStatus: miscProps.connectionStatus,
  };

  // Render the active sub-tab content
  const renderContent = () => {
    switch (activeMiscTab) {
      case "photos":
        return (
          <PhotosTab
            callbacks={photosCallbacks}
            photosActions={actions.photos}
            showLoading={miscProps.showLoading}
            hideLoading={miscProps.hideLoading}
            showNotification={miscProps.showNotification}
          />
        );
      case "wasteDisposal":
        if (!isWasteDisposalEnabled) {
          return (
            <div className={styles.disabledContent}>
              <p>Enable the Waste Disposal toggle to access this section.</p>
            </div>
          );
        }
        return (
          <WasteDisposalTab
            callbacks={wasteDisposalCallbacks}
            actions={actions.wasteDisposal}
          />
        );
      case "clientSignatures":
        if (!isClientSignaturesEnabled) {
          return (
            <div className={styles.disabledContent}>
              <p>Enable the Client Signatures toggle to access this section.</p>
            </div>
          );
        }
        return <ClientSignaturesTab callbacks={clientSignaturesCallbacks} />;
      case "sds":
        return (
          <SdsTab
            callbacks={sdsCallbacks}
            sdsActions={actions.sdsReferences}
            showNotification={miscProps.showNotification}
          />
        );
      default:
        return (
          <PhotosTab
            callbacks={photosCallbacks}
            photosActions={actions.photos}
            showLoading={miscProps.showLoading}
            hideLoading={miscProps.hideLoading}
            showNotification={miscProps.showNotification}
          />
        );
    }
  };

  return (
    <div className={styles.miscTabContainer}>
      {/* Toggle switches header */}
      <MiscToggleSection
        miscProps={miscProps}
        projectActions={actions.projects}
      />

      {/* Sub-tab menu bar */}
      <div className={styles.subTabMenuBar}>
        <MiscSubTabButton
          tabName="photos"
          label="Photos"
          isActive={activeMiscTab === "photos"}
          isDisabled={false}
          onClick={handleSubTabChange}
        />
        <MiscSubTabButton
          tabName="sds"
          label="SDS"
          isActive={activeMiscTab === "sds"}
          isDisabled={false}
          onClick={handleSubTabChange}
        />
        <MiscSubTabButton
          tabName="wasteDisposal"
          label="Waste Disposal"
          isActive={activeMiscTab === "wasteDisposal"}
          isDisabled={!isWasteDisposalEnabled}
          onClick={handleSubTabChange}
        />
        <MiscSubTabButton
          tabName="clientSignatures"
          label="Client Signatures"
          isActive={activeMiscTab === "clientSignatures"}
          isDisabled={!isClientSignaturesEnabled}
          onClick={handleSubTabChange}
        />
      </div>

      {/* Sub-tab content */}
      <div className={styles.subTabContent}>{renderContent()}</div>
    </div>
  );
};

export default MiscTab;